/**
 * SMC + Options Confluence — Smart Money Concepts strategy backtest.
 * BOS/CHoCH, Order Blocks, FVGs, Liquidity Sweeps, Premium/Discount.
 * All P&L in index points.
 */

import React, { useState } from "react";
import axios from "axios";

const API = "/api/zerodha";

function exportCSV(trades, index) {
  if (!trades.length) return;
  const h = "Index,Direction,Setup,Entry Time,Exit Time,Entry Spot,Exit Spot,SL,T1,T2,Risk Pts,P&L Pts,R-Multiple,MFE,MAE,OB High,OB Low,Exit Reason\n";
  const rows = trades.map(t =>
    `${t.index},${t.direction},${t.setup},${t.entry_time},${t.exit_time},${t.entry_spot},${t.exit_spot},${t.sl_spot},${t.t1_spot},${t.t2_spot},${t.risk_pts},${t.pnl_pts},${t.r_multiple},${t.max_favorable_pts},${t.max_adverse_pts},${t.ob_high},${t.ob_low},${t.exit_reason}`
  ).join("\n");
  const blob = new Blob([h + rows], { type: "text/csv" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `smc-${index}-backtest.csv`; a.click();
}

function Badge({ text, color }) {
  return <span style={{ display: "inline-block", padding: "2px 6px", borderRadius: 4, fontSize: 9, fontWeight: 700, background: color + "22", color, border: `1px solid ${color}` }}>{text}</span>;
}

export default function SMCStrategy() {
  const [index, setIndex] = useState("NIFTY");
  const [days, setDays] = useState("30");
  const [slAtr, setSlAtr] = useState("1.0");
  const [t1r, setT1r] = useState("1.5");
  const [t2r, setT2r] = useState("3");
  const [pivot5m, setPivot5m] = useState("2");
  const [pivot15m, setPivot15m] = useState("3");
  const [imbMult, setImbMult] = useState("1.5");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const run = async () => {
    setLoading(true); setError(""); setData(null);
    try {
      const res = await axios.get(`${API}/smc-backtest`, {
        params: { index, days, sl_atr: slAtr, t1r, t2r, pivot_5m: pivot5m, pivot_15m: pivot15m, imbalance_mult: imbMult },
        timeout: 120000,
      });
      if (res.data.success) setData(res.data.data);
      else setError(res.data.message);
    } catch (err) { setError(err.response?.data?.message || err.message); }
    setLoading(false);
  };

  const s = data?.summary;

  return (
    <div className="five-ema">
      <div className="dashboard-header">
        <div>
          <h2>SMC + Options Confluence</h2>
          <p className="dashboard-subtitle">Smart Money Concepts — BOS/CHoCH, Order Blocks, FVGs, Liquidity Sweeps</p>
        </div>
      </div>

      {/* Config */}
      <div style={{ padding: "12px", background: "var(--panel3)", borderBottom: "1px solid var(--surface3)" }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <label style={lbl}>Index
            <select className="ema-select" value={index} onChange={e => setIndex(e.target.value)}>
              <option value="NIFTY">NIFTY</option><option value="BANKNIFTY">BANKNIFTY</option><option value="SENSEX">SENSEX</option>
            </select>
          </label>
          <label style={lbl}>Days
            <select className="ema-select" value={days} onChange={e => setDays(e.target.value)}>
              <option value="5">5</option><option value="10">10</option><option value="20">20</option>
              <option value="30">30</option><option value="60">60</option>
            </select>
          </label>
          <label style={lbl}>SL (ATR ×)
            <select className="ema-select" value={slAtr} onChange={e => setSlAtr(e.target.value)}>
              <option value="0.5">0.5</option><option value="0.75">0.75</option><option value="1.0">1.0</option><option value="1.5">1.5</option>
            </select>
          </label>
          <label style={lbl}>T1 R
            <select className="ema-select" value={t1r} onChange={e => setT1r(e.target.value)}>
              <option value="1">1.0</option><option value="1.5">1.5</option><option value="2">2.0</option>
            </select>
          </label>
          <label style={lbl}>T2 R
            <select className="ema-select" value={t2r} onChange={e => setT2r(e.target.value)}>
              <option value="2">2.0</option><option value="3">3.0</option><option value="4">4.0</option>
            </select>
          </label>
          <button className="btn btn-sm" style={{ fontSize: 10, color: "var(--grey-text2)" }} onClick={() => setShowAdvanced(!showAdvanced)}>
            {showAdvanced ? "Hide" : "Show"} SMC Params
          </button>
          <button className="btn btn-primary" onClick={run} disabled={loading}>{loading ? "Running..." : "Run Backtest"}</button>
        </div>
        {showAdvanced && (
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8 }}>
            <label style={lbl}>Pivot N (5m)
              <select className="ema-select" value={pivot5m} onChange={e => setPivot5m(e.target.value)}>
                <option value="1">1</option><option value="2">2</option><option value="3">3</option>
              </select>
            </label>
            <label style={lbl}>Pivot N (15m)
              <select className="ema-select" value={pivot15m} onChange={e => setPivot15m(e.target.value)}>
                <option value="2">2</option><option value="3">3</option><option value="4">4</option>
              </select>
            </label>
            <label style={lbl}>Imbalance (ATR ×)
              <select className="ema-select" value={imbMult} onChange={e => setImbMult(e.target.value)}>
                <option value="1.0">1.0</option><option value="1.5">1.5</option><option value="2.0">2.0</option>
              </select>
            </label>
          </div>
        )}
      </div>

      {error && <div className="error-msg">{error}</div>}
      {loading && <div className="loading"><div className="spinner" /><p>Running SMC backtest on 5-min + 15-min data...</p></div>}

      {s && !loading && (
        <div className="backtest-results">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", padding: "8px 12px" }}>
            <h3>Results — {s.index} ({s.days} days, {s.totalCandles} candles)</h3>
            <span style={{ fontSize: 10, color: "var(--grey-text2)" }}>SL: {s.slAtrMult}×ATR | T1: {s.t1R}R | T2: {s.t2R}R | Pivot: {s.pivotN5m}/{s.pivotN15m} | POINTS</span>
          </div>

          {s.totalPnlPts < 0 && (
            <div style={{ padding: "10px 16px", margin: "0 12px 8px", borderRadius: 6, background: "var(--tint-neg)", border: "1px solid #dc2626", color: "var(--pale-neg)", fontSize: 13 }}>
              Strategy is unprofitable on this period. Total: {s.totalPnlPts} pts. Shown honestly.
            </div>
          )}

          <div className={`bt-money-banner ${s.totalPnlPts >= 0 ? "money-profit" : "money-loss"}`}>
            <div className="bt-money-amount">{s.totalPnlPts >= 0 ? "+" : ""}{s.totalPnlPts} pts</div>
            <div className="bt-money-detail">{s.totalTrades} trades | Avg R: {s.avgR} | PF: {s.profitFactor === Infinity ? "INF" : s.profitFactor}</div>
          </div>

          <div className="bt-summary-grid">
            <div className="bt-card"><div className="bt-card-value">{s.totalTrades}</div><div className="bt-card-label">Trades</div></div>
            <div className={`bt-card ${s.winRate >= 45 ? "bt-card-green" : "bt-card-red"}`}><div className="bt-card-value">{s.winRate}%</div><div className="bt-card-label">Win Rate</div></div>
            <div className={`bt-card ${s.profitFactor >= 1 ? "bt-card-green" : "bt-card-red"}`}><div className="bt-card-value">{s.profitFactor === Infinity ? "INF" : s.profitFactor}</div><div className="bt-card-label">Profit Factor</div></div>
            <div className="bt-card"><div className="bt-card-value">{s.avgWinPts} pts</div><div className="bt-card-label">Avg Win</div></div>
            <div className="bt-card"><div className="bt-card-value">{Math.abs(s.avgLossPts || 0)} pts</div><div className="bt-card-label">Avg Loss</div></div>
            <div className="bt-card bt-card-red"><div className="bt-card-value">{s.maxDrawdownPts} pts</div><div className="bt-card-label">Max DD</div></div>
          </div>

          <div className="bt-dir-pnl-cards">
            <div className={`bt-dir-card bt-dir-card-long ${s.bullWinRate >= 45 ? "bt-card-green" : "bt-card-red"}`}>
              <div className="bt-dir-card-title">BULLISH (OB+Sweep at Discount)</div>
              <div className="bt-dir-card-meta">{s.bullTrades} trades | {s.bullWinRate}% win</div>
            </div>
            <div className={`bt-dir-card bt-dir-card-short ${s.bearWinRate >= 45 ? "bt-card-green" : "bt-card-red"}`}>
              <div className="bt-dir-card-title">BEARISH (OB+Sweep at Premium)</div>
              <div className="bt-dir-card-meta">{s.bearTrades} trades | {s.bearWinRate}% win</div>
            </div>
          </div>

          {/* Setup breakdown */}
          {s.setupStats && Object.keys(s.setupStats).length > 0 && (
            <div style={{ padding: "8px 12px" }}>
              <h4 style={{ margin: "8px 0 4px", fontSize: 13, color: "var(--grey-text)" }}>By Setup Type</h4>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {Object.entries(s.setupStats).map(([setup, st]) => (
                  <div key={setup} style={{ padding: "6px 10px", background: "var(--panel3)", borderRadius: 6, border: "1px solid var(--surface3)", fontSize: 11 }}>
                    <div style={{ color: "#8b5cf6", fontWeight: 600 }}>{setup}</div>
                    <div style={{ color: "var(--grey-text)" }}>
                      {st.trades} trades | {st.trades > 0 ? Math.round(st.wins / st.trades * 100) : 0}% win |{" "}
                      <span className={st.pnl >= 0 ? "positive" : "negative"}>{st.pnl > 0 ? "+" : ""}{st.pnl} pts</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Equity curve */}
          {data.equity?.length > 0 && (
            <div className="bt-equity">
              <h4>Equity Curve ({data.equity.length} trades)</h4>
              <div className="bt-equity-chart">
                {(() => {
                  const eq = data.equity;
                  const vals = [...eq.map(p => p.pnl), 0];
                  const maxV = Math.max(...vals), minV = Math.min(...vals);
                  const range = maxV - minV || 1, h = 160, w = Math.max(eq.length * 16 + 20, 300);
                  const yOf = v => h - ((v - minV) / range) * h;
                  return (
                    <svg width="100%" height={h + 30} viewBox={`0 0 ${w} ${h + 30}`}>
                      <line x1="0" y1={yOf(0)} x2={w} y2={yOf(0)} stroke="var(--border-strong)" strokeDasharray="4" />
                      <polyline fill="none" stroke={eq[eq.length - 1]?.pnl >= 0 ? "#69f0ae" : "#ff5252"} strokeWidth="2"
                        points={eq.map((p, i) => `${i * 16 + 10},${yOf(p.pnl)}`).join(" ")} />
                      {eq.map((p, i) => (
                        <circle key={i} cx={i * 16 + 10} cy={yOf(p.pnl)} r="3" fill={p.type === "BULLISH" ? "#69f0ae" : "#ff5252"}>
                          <title>{`${p.type}: ${p.pnl >= 0 ? "+" : ""}${p.pnl} pts`}</title>
                        </circle>
                      ))}
                    </svg>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Trade log */}
          <div className="bt-trade-log">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h4>Trade Log ({data.trades?.length})</h4>
              <button className="btn btn-export" onClick={() => exportCSV(data.trades, index)}>Export CSV</button>
            </div>
            <div className="stocks-table-wrapper">
              <table className="stocks-table" style={{ fontSize: 11 }}>
                <thead><tr>
                  <th>Dir</th><th>Setup</th><th>Entry</th><th>Exit</th>
                  <th>Entry Spot</th><th>Exit Spot</th><th>SL</th><th>T1</th><th>T2</th><th>Risk</th>
                  <th>P&L</th><th>R</th><th>MFE</th><th>MAE</th><th>OB Zone</th><th>Exit Reason</th>
                </tr></thead>
                <tbody>
                  {data.trades?.slice().reverse().map((t, i) => (
                    <tr key={i} className={t.result === "WIN" ? "trade-win" : "trade-loss"}>
                      <td><span className={`signal-badge ${t.direction === "BULLISH" ? "badge-long" : "badge-short"}`}>{t.direction === "BULLISH" ? "BULL" : "BEAR"}</span></td>
                      <td>{t.setup?.split("+").map(s => <Badge key={s} text={s} color={s === "SWEEP" ? "#f59e0b" : s === "BOS" ? "#60a5fa" : s === "FVG" ? "#a78bfa" : "#34d399"} />)}</td>
                      <td style={{ fontSize: 10 }}>{t.entry_time ? new Date(t.entry_time).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "-"}</td>
                      <td style={{ fontSize: 10 }}>{t.exit_time ? new Date(t.exit_time).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "-"}</td>
                      <td>{t.entry_spot}</td>
                      <td>{t.exit_spot}</td>
                      <td className="sl">{t.sl_spot}</td>
                      <td>{t.t1_spot}</td>
                      <td>{t.t2_spot}</td>
                      <td>{t.risk_pts} pts</td>
                      <td className={t.pnl_pts >= 0 ? "positive" : "negative"}>{t.pnl_pts > 0 ? "+" : ""}{t.pnl_pts} pts</td>
                      <td style={{ color: t.r_multiple >= 0 ? "#34d399" : "#f87171" }}>{(t.r_multiple || 0).toFixed(1)}R</td>
                      <td style={{ color: "#34d399", fontSize: 10 }}>{t.max_favorable_pts}</td>
                      <td style={{ color: "#f87171", fontSize: 10 }}>{t.max_adverse_pts}</td>
                      <td style={{ fontSize: 9, color: "var(--grey-text2)" }}>{t.ob_high}-{t.ob_low}</td>
                      <td style={{ fontSize: 10 }}>{t.exit_reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ padding: "12px", fontSize: 10, color: "var(--edge)", textAlign: "center", borderTop: "1px solid var(--surface2)", marginTop: 12 }}>
            All P&L in index points. Options confluence (OI/PCR/Max Pain) skipped in backtest — not available historically.
            Backtest results are simulations. Not investment advice.
          </div>
        </div>
      )}

      {!data && !loading && !error && (
        <div style={{ padding: 24, color: "var(--btn-border)" }}>
          <h4 style={{ color: "var(--grey-text)" }}>SMC Entry Requirements (all must align):</h4>
          <ol style={{ fontSize: 12, lineHeight: 1.8 }}>
            <li><strong>HTF Bias:</strong> 15-min structure (HH/HL = bullish, LH/LL = bearish)</li>
            <li><strong>Premium/Discount:</strong> LONG only in discount zone, SHORT only in premium zone</li>
            <li><strong>Order Block:</strong> Price touching a live bullish/bearish OB</li>
            <li><strong>Liquidity Sweep:</strong> Sell-side swept (for longs) or buy-side swept (for shorts) within last 3 bars</li>
            <li><strong>BOS Confirmation:</strong> 5-min Break of Structure in trade direction within last 3 bars</li>
          </ol>
          <p style={{ fontSize: 11 }}>Exits: SL at structural level or {slAtr}×ATR | T1: {t1r}R (nearest FVG) | T2: {t2r}R | Trail after T1 | Flat by 15:15</p>
        </div>
      )}
    </div>
  );
}

const lbl = { display: "flex", flexDirection: "column", gap: 2, fontSize: 10, color: "var(--grey-text2)" };
